import * as Sentry from '@sentry/react';
import constants from 'config/constants';
import store from 'redux/store';
import { logout } from 'utils/logout';
import SessionManager from './SessionManager';
import { SESSION_STATE, END_REASON, STORAGE_KEYS } from './constants';
import { createOperation, emitOperationEvent } from 'utils/telemetry';
import { EVENTS } from 'utils/telemetry/catalog';

const { API_URI, AUTH_TOKEN, HIDDEN_COMPONENT_ON_LTI } = constants;

/**
 * Whether this tab is the app running inside an LTI launch.
 *
 * Read here, in the wiring, and never inside SessionManager: the state machine takes
 * everything through its constructor and knows nothing about this app, which is what
 * makes it testable without a DOM.
 *
 * Two signals, because neither is sufficient alone. Redux's `ltiPlatform` — the flag
 * apollo/index.js uses — is in-memory and only dispatched on the transition into LTI
 * mode, and that same branch reloads the page (platform.js:110), so it reads false for
 * the rest of the embedded session. The stored flag survives reloads but can be left
 * stale in an ordinary tab, so it is paired with an actual frame check.
 *
 * Deliberately conservative: when both signals miss, the session ends the way it does
 * today. A false negative costs the fix, never a regression.
 */
const isEmbeddedInLti = () => {
  let storedFlag = null;
  try {
    storedFlag = localStorage.getItem(HIDDEN_COMPONENT_ON_LTI);
  } catch {
    // Storage unavailable; fall through to the Redux flag.
  }

  if (storedFlag === 'YES') {
    try {
      // The stored flag alone is not enough: platform.js can leave it set in an
      // ordinary tab, which is the very case it resets on mount.
      if (window.self !== window.top) return true;
    } catch {
      // Reading window.top across origins throws, and only a framed page can be
      // cross-origin with its parent — the throw is the answer.
      return true;
    }
  }

  try {
    return Boolean(store.getState().general.ltiPlatform);
  } catch {
    return false;
  }
};

const RENEW_MUTATION = `
  mutation RenewSession {
    renewSession {
      token
      expires_at
      session_started_at
      reason
    }
  }
`;

/**
 * Renews the token over plain fetch rather than through the Apollo client.
 *
 * Going through Apollo would send the renewal through the error link, so a
 * failed renewal would end the session, which would... trigger more of the same.
 * Keeping it on its own transport makes the failure path explicit: throwing
 * means "recoverable, retry"; returning a reason means "stop".
 */
const renewViaFetch = async () => {
  const token = window.localStorage.getItem(AUTH_TOKEN);
  const response = await fetch(`${API_URI}/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: token || '',
    },
    body: JSON.stringify({ query: RENEW_MUTATION }),
  });

  // Transport-level failure. Throwing hands it to the retry budget.
  if (!response.ok) {
    throw new Error(`renewal failed with status ${response.status}`);
  }

  const body = await response.json();
  const payload = body && body.data && body.data.renewSession;

  // No payload means the request succeeded but the server refused. That is an
  // application answer, not a blip, so it must not be retried.
  if (!payload) return { token: null, reason: END_REASON.RENEWAL_FAILED };

  return payload;
};

/**
 * Session telemetry. Records what happened and why, never the token itself or
 * anything derived from it.
 */
let renewalOperation = null;

const logSessionEvent = (event, data) => {
  Sentry.addBreadcrumb({
    category: 'session',
    level: 'info',
    message: event,
    data,
  });

  if (event === 'renewal_started') {
    renewalOperation = createOperation('session');
    emitOperationEvent(renewalOperation, EVENTS.SESSION_EXTENSION_STARTED);
  } else if (event === 'renewal_ok' && renewalOperation) {
    emitOperationEvent(renewalOperation, EVENTS.SESSION_RECOVERY_SUCCEEDED);
    renewalOperation = null;
  } else if (
    renewalOperation &&
    (event === 'renewal_gave_up' || event === 'renewal_refused_final')
  ) {
    emitOperationEvent(renewalOperation, EVENTS.SESSION_RECOVERY_FAILED, {
      failure_category: data?.reason || 'renewal_failed',
    });
    renewalOperation = null;
  } else if (event === 'session_ended' && data?.reason !== END_REASON.LOGOUT) {
    if (renewalOperation) {
      emitOperationEvent(renewalOperation, EVENTS.SESSION_RECOVERY_FAILED, {
        failure_category: data?.reason || 'renewal_failed',
      });
      renewalOperation = null;
    }
    const expirationOperation = createOperation('session');
    emitOperationEvent(expirationOperation, EVENTS.SESSION_EXPIRED, {
      failure_category: data?.reason || 'unknown',
    });
  }
};

export const sessionManager = new SessionManager({
  getToken: () => {
    try {
      return window.localStorage.getItem(AUTH_TOKEN);
    } catch {
      return null;
    }
  },
  setToken: (token) => {
    try {
      window.localStorage.setItem(AUTH_TOKEN, token);
    } catch {
      // Nothing useful to do: the in-memory session keeps working until reload.
    }
  },
  renew: renewViaFetch,
  logger: logSessionEvent,
  onEnd: (reason) => {
    // One exit, whatever ended the session. logout() navigates hard, which
    // discards every timer, listener and in-flight response in one go — no
    // cancellation bookkeeping required.
    //
    // The embedded case only changes where that exit leads. apollo/index.js has
    // guarded the reactive path — a query failing with AUTHENTICATION_REQUIRED —
    // since long before this manager existed, but the timer reaches the end of a
    // session first and by its own route, so without this an idle or ceiling
    // expiry hard-navigated Blackboard's iframe to a login form the user has no
    // way to use.
    logout({ reason, embedded: isEmbeddedInLti() });
  },
});

export { SESSION_STATE, END_REASON, STORAGE_KEYS };
export default sessionManager;
